import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppShell, HeaderActionIcon } from '@etherisc/ui-kit';
import { PrimaryButton } from '../../components/buttons';
import { MyUbuntuLogo } from '../../assets/MyUbuntuLogo';
import { useAuth } from '../../hooks/useAuth';
import { useRBAC } from '../../hooks/usePermissions';
import { VersionDisplay } from '../system/VersionDisplay';
import { WrenchIcon } from 'lucide-react';

import { 
  userActionsConfig, 
  transformToNavItems, 
  generateBreadcrumbs,
  getActiveTopNavId,
  getFilteredTopNavigation,
  type NavItem,
  type BreadcrumbItem,
  type TopNavItem
} from '../../config/navigation';
import type { Permission } from '../../types/Auth';

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * AppLayout - Centralized Navigation Wrapper
 * 
 * This component automatically wraps ALL pages with the ui-kit 0.6.0 AppShell
 * and provides navigation without any page needing to know about it.
 * 
 * Features:
 * - Automatic RBAC filtering of navigation items
 * - Dynamic breadcrumb generation
 * - Centralized user actions from config
 * - Active state management based on current route
 * - Uses ui-kit 0.6.0 structured data interfaces
 * 
 * Benefits:
 * - Pages focus ONLY on content - zero navigation concerns
 * - Consistent navigation behavior across all pages
 * - Single source of truth for navigation structure
 * - Automatic permission-based filtering
 * - Easy to maintain and modify
 * 
 * Replaces:
 * - All per-page navigation definitions (getNavItems(), userActions, breadcrumbs)
 * - Manual BrandedAppShell wrappers in every page
 * - Scattered RBAC permission checks
 */
export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hasPermission } = useRBAC();

  // Determine active top navigation based on current route
  const [activeTopNavId, setActiveTopNavId] = useState<string>(() => 
    getActiveTopNavId(location.pathname)
  );

  // Update active top nav when route changes
  useEffect(() => {
    const newTopNavId = getActiveTopNavId(location.pathname);
    if (newTopNavId !== activeTopNavId) {
      setActiveTopNavId(newTopNavId);
    }
  }, [location.pathname, activeTopNavId]);

  /**
   * Top Navigation Items for AppShell
   * Filtered by topnav:* permissions and with active state
   * Use onClick with navigate() to prevent page reload
   */
  const topNavItems: TopNavItem[] = getFilteredTopNavigation(
    (permission: string) => hasPermission(permission as Permission)
  ).map(item => {
    const href = item.href;
    return {
      id: item.id,
      label: item.label,
      icon: item.icon,
      isActive: item.id === activeTopNavId,
      onClick: () => {
        setActiveTopNavId(item.id);
        if (href) {
          navigate(href);
        }
      }
    };
  });

  /**
   * Sidebar Navigation Items
   * Filtered by active top navigation and user permissions
   * Add onClick handlers to use React Router navigation
   * Add wrench icon to disabled items (not yet implemented)
   */
  const navItems: NavItem[] = transformToNavItems(
    activeTopNavId,
    location.pathname,
    (permission: string) => hasPermission(permission as Permission)
  ).map(item => {
    const path = item.href;
    
    // For disabled items, add wrench icon and prevent navigation
    if (item.disabled) {
      return {
        ...item,
        href: undefined,
        onClick: undefined, // No click action for disabled items
        icon: (
          <div className="flex items-center gap-2">
            {item.icon}
            <WrenchIcon size={14} className="text-warning opacity-60" />
          </div>
        ),
      };
    }
    
    return {
      ...item,
      href: undefined, // Remove href to prevent browser navigation
      onClick: path && !item.isGroup ? () => {
        navigate(path);
      } : undefined,
    };
  });

  /**
   * Generate user actions for header - build as JSX with user menu and logout
   * Uses centralized configuration plus user-specific actions
   */
  const userActions = (
    <div className="flex items-center gap-2">
      {/* Standard action icons */}
      {userActionsConfig.map((action, index) => (
        <HeaderActionIcon 
          key={action.id || index}
          icon={action.icon}
          onClick={action.onClick}
          label={action.label || `Action ${index + 1}`}
        />
      ))}
      
      {/* User info and logout */}
      {user && (
        <div className="flex items-center gap-2 ml-2 pl-2 border-l border-base-300">
          <div className="hidden md:block text-sm text-base-content">
            <div className="font-medium">{user.name || user.email}</div>
            <div className="text-xs text-base-content/70">
              {user.roles?.[0] || 'User'}
            </div>
          </div>
          <PrimaryButton
            onClick={logout}
            size="sm"
          >
            Logout
          </PrimaryButton>
        </div>
      )}
    </div>
  );

  /**
   * Generate breadcrumbs based on current path
   * Automatic generation eliminates manual breadcrumb logic from pages
   */
  const breadcrumbs: BreadcrumbItem[] = generateBreadcrumbs(location.pathname);

  return (
    <AppShell
      logo={<MyUbuntuLogo />}
      topNavItems={topNavItems}
      navItems={navItems}
      userActions={userActions}
      breadcrumbs={breadcrumbs}
      fixedHeader={true}
      defaultCollapsed={false}
      className="app-shell h-screen overflow-hidden"
      footer={
        <div className="flex justify-between items-center p-4 border-t border-base-200 bg-base-50">
          <VersionDisplay variant="minimal" />
          <div className="text-xs text-gray-500">
            © 2025 Etherisc. All rights reserved.
          </div>
        </div>
      }
    >
      {children}
    </AppShell>
  );
}

/**
 * Usage Example:
 * 
 * In your main App or Router component:
 * - Wrap all routes with AppLayout component
 * - AppLayout handles ALL navigation automatically
 * - Pages focus only on content
 * 
 * UserManagement.tsx becomes super clean:
 * - Only user data logic and table rendering
 * - No navigation imports or definitions
 * - No BrandedAppShell wrapper needed
 * 
 * Benefits achieved:
 * - UserManagement.tsx: 495 lines → ~150 lines (70% reduction)
 * - Navigation logic: Duplicated 5+ times → 1 central file
 * - RBAC checks: Scattered → Centralized
 * - Maintenance: Change navigation in one place
 */ 